import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { startupAPI } from '../lib/api';
import { getApiUrl } from '../config/api.config';
import Logo from '../components/Logo';
import IFSCVerification from '../components/IFSCVerification';

const StartupCompleteProfile = () => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const { tenantId } = useParams();
	const [currentStep, setCurrentStep] = useState(1);
	const modalContentRef = useRef(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [industryOptions, setIndustryOptions] = useState([]);
	const [stageOptions, setStageOptions] = useState([]);
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		phoneNumber: '',
		companyName: '',
		about: '',
		website: '',
		industry: '',
		stage: '',
		foundedYear: new Date().getFullYear().toString(),
		valuations: '',
		cin: '',
		gstNumber: '',
		panNumber: '',
		dpiitNumber: '',
		bankAccounts: [{ accountHolderName: '', bankAccountNumber: '', ifscCode: '', bankName: '', branchName: '' }],
		officialAddress: '',
		branchAddress: '',
		city: '',
		state: '',
		pincode: '',
		country: 'India',
		logo: null,
		logoPreview: '',
		directors: [{ name: '', designation: '', email: '', phone: '', shareholding: '', category: '', panNumber: '', aadharNumber: '' }]
	});

	useEffect(() => {
		if (modalContentRef.current) {
			modalContentRef.current.scrollTop = 0;
		}
	}, [currentStep]);

	useEffect(() => {
		const load = async () => {
			if (!user) return;
			try {
				const [profile, directorsRes, settingsRes] = await Promise.all([
					startupAPI.getMyStartupProfile().catch(() => null),
					startupAPI.getMyStartupDirectors().catch(() => []),
					fetch(getApiUrl('settings/grouped'), { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json()).catch(() => ({}))
				]);

				if (settingsRes.INDUSTRY) setIndustryOptions(settingsRes.INDUSTRY);
				if (settingsRes.STARTUP_STAGE) setStageOptions(settingsRes.STARTUP_STAGE);

				const directors = Array.isArray(directorsRes) ? directorsRes : [];
				const foundedYear = profile?.foundedDate
					? (typeof profile.foundedDate === 'string' ? profile.foundedDate.substring(0, 4) : String(new Date(profile.foundedDate).getFullYear()))
					: new Date().getFullYear().toString();

				// Parse full profile JSON if stored (includes bankAccounts, cin, gst, etc.)
				let additional = {};
				try {
					const desc = profile?.description;
					if (typeof desc === 'string' && desc.trim().startsWith('{')) {
						additional = JSON.parse(desc);
					} else if (desc) {
						additional = { about: desc };
					}
				} catch (e) {
					additional = profile?.description ? { about: profile.description } : {};
				}

				const defaultBankAccounts = [{ accountHolderName: '', bankAccountNumber: '', ifscCode: '', bankName: '', branchName: '' }];
				const bankAccounts = (additional.bankAccounts && additional.bankAccounts.length > 0)
					? additional.bankAccounts.map(ba => ({
						accountHolderName: ba.accountHolderName || '',
						bankAccountNumber: ba.bankAccountNumber || '',
						ifscCode: ba.ifscCode || '',
						bankName: ba.bankName || '',
						branchName: ba.branchName || ''
					}))
					: (additional.accountHolderName || additional.bankAccountNumber
						? [{ accountHolderName: additional.accountHolderName || '', bankAccountNumber: additional.bankAccountNumber || '', ifscCode: additional.ifscCode || '', bankName: additional.bankName || '', branchName: additional.branchName || '' }]
						: defaultBankAccounts);

				setFormData(prev => ({
					...prev,
					name: profile?.name ?? prev.name,
					email: profile?.email ?? prev.email,
					phoneNumber: profile?.phoneNumber ?? prev.phoneNumber,
					companyName: profile?.organizationName ?? prev.companyName,
					industry: profile?.department ?? prev.industry,
					stage: profile?.jobTitle ?? prev.stage,
					about: additional.about ?? profile?.description ?? prev.about,
					website: profile?.website ?? additional.website ?? prev.website,
					valuations: profile?.valuations ?? additional.valuations ?? prev.valuations,
					foundedYear: foundedYear || additional.foundedYear || prev.foundedYear,
					cin: additional.cin ?? prev.cin,
					gstNumber: additional.gstNumber ?? prev.gstNumber,
					panNumber: additional.panNumber ?? prev.panNumber,
					dpiitNumber: additional.dpiitNumber ?? prev.dpiitNumber,
					officialAddress: profile?.officialAddress ?? additional.officialAddress ?? prev.officialAddress,
					branchAddress: profile?.branchAddress ?? additional.branchAddress ?? prev.branchAddress,
					city: profile?.city ?? additional.city ?? prev.city,
					state: profile?.state ?? additional.state ?? prev.state,
					pincode: additional.pincode ?? prev.pincode,
					country: profile?.country ?? additional.country ?? prev.country,
					bankAccounts,
					directors: directors.length > 0
						? directors.map(d => ({
							name: d.name || '',
							designation: d.role || '',
							email: d.email || '',
							phone: d.phone || '',
							shareholding: d.shareholding || '',
							category: d.category || '',
							panNumber: d.panNumber || '',
							aadharNumber: d.aadharNumber || ''
						}))
						: prev.directors
				}));
			} catch (e) {
				setError(e.message || 'Failed to load profile');
			}
			setLoading(false);
		};
		load();
	}, [user]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({ ...prev, [name]: value }));
	};

	const handleLogoChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setFormData(prev => ({
				...prev,
				logo: file,
				logoPreview: URL.createObjectURL(file)
			}));
		}
	};

	const handleDirectorChange = (index, field, value) => {
		const next = [...formData.directors];
		next[index] = { ...next[index], [field]: value };
		setFormData(prev => ({ ...prev, directors: next }));
	};

	const addDirector = () => {
		setFormData(prev => ({
			...prev,
			directors: [...prev.directors, { name: '', designation: '', email: '', phone: '', shareholding: '', category: '', panNumber: '', aadharNumber: '' }]
		}));
	};

	const removeDirector = (index) => {
		if (formData.directors.length <= 1) return;
		setFormData(prev => ({ ...prev, directors: prev.directors.filter((_, i) => i !== index) }));
	};

	const addBankAccount = () => {
		setFormData(prev => ({
			...prev,
			bankAccounts: [...prev.bankAccounts, { accountHolderName: '', bankAccountNumber: '', ifscCode: '', bankName: '', branchName: '' }]
		}));
	};

	const removeBankAccount = (index) => {
		setFormData(prev => {
			const next = prev.bankAccounts.filter((_, i) => i !== index);
			return { ...prev, bankAccounts: next.length ? next : [{ accountHolderName: '', bankAccountNumber: '', ifscCode: '', bankName: '', branchName: '' }] };
		});
	};

	const handleBankAccountChange = (index, field, value) => {
		setFormData(prev => {
			const next = [...prev.bankAccounts];
			next[index] = { ...next[index], [field]: value };
			return { ...prev, bankAccounts: next };
		});
	};

	const buildDescription = () => ({
		about: formData.about,
		website: formData.website,
		foundedYear: formData.foundedYear || new Date().getFullYear().toString(),
		valuations: formData.valuations,
		cin: formData.cin,
		gstNumber: formData.gstNumber,
		panNumber: formData.panNumber,
		dpiitNumber: formData.dpiitNumber,
		bankAccounts: formData.bankAccounts,
		officialAddress: formData.officialAddress,
		branchAddress: formData.branchAddress,
		city: formData.city,
		state: formData.state,
		pincode: formData.pincode,
		country: formData.country,
		directors: formData.directors
	});

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		setSaving(true);
		try {
			await startupAPI.updateMyStartupProfile({
				name: formData.name.trim(),
				phoneNumber: formData.phoneNumber || null,
				organizationName: formData.companyName.trim(),
				department: formData.industry || null,
				jobTitle: formData.stage || null,
				description: JSON.stringify(buildDescription())
			});
			const profile = await startupAPI.getMyStartupProfile();
			const startupId = profile?.startupId;
			if (formData.logo && startupId) {
				const logoFormData = new FormData();
				logoFormData.append('file', formData.logo);
				logoFormData.append('moduleType', 'STARTUP');
				logoFormData.append('moduleId', startupId);
				logoFormData.append('fileCategory', 'LOGO');
				logoFormData.append('visibility', 'PUBLIC');
				await fetch(getApiUrl('files/upload'), {
					method: 'POST',
					headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
					body: logoFormData
				});
			}
			navigate(`/${tenantId || 'default'}/startup/dashboard`);
		} catch (err) {
			setError(err.response?.data?.error || err.message || 'Failed to save profile');
		} finally {
			setSaving(false);
		}
	};

	const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 4));
	const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

	if (loading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-600 border-t-transparent" />
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50 py-8 px-4 sm:px-6 lg:px-8">
			<div className="max-w-4xl mx-auto">
				<div className="flex items-center justify-center mb-6">
					<Logo showText={true} width={64} height={64} />
				</div>
				<div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
					<div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
						<h1 className="text-xl font-bold text-white">Complete your startup profile</h1>
						<p className="text-blue-100 text-sm mt-1">Step {currentStep} of 4</p>
						<div className="mt-4 flex space-x-2">
							{[1, 2, 3, 4].map(step => (
								<div key={step} className={`flex-1 h-2 rounded-full ${step <= currentStep ? 'bg-white' : 'bg-blue-400'}`} />
							))}
						</div>
					</div>
					<div ref={modalContentRef} className="px-6 py-6 sm:px-10 sm:py-8 max-h-[70vh] overflow-y-auto">
						{error && (
							<div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800">{error}</div>
						)}

						<form onSubmit={handleSubmit} className="space-y-6">
							{/* Step 1: Contact & Company */}
							{currentStep === 1 && (
								<div className="space-y-6">
									<div>
										<h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Person Details</h2>
										<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
												<input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="John Doe" />
											</div>
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
												<input type="email" name="email" value={formData.email} readOnly className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600" />
											</div>
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
												<input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="+91 98765 43210" />
											</div>
										</div>
									</div>
									<div>
										<h2 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h2>
										<div className="space-y-4">
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">Company Name <span className="text-red-500">*</span></label>
												<input type="text" name="companyName" value={formData.companyName} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Startup Inc." />
											</div>
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">About Company</label>
												<textarea name="about" value={formData.about} onChange={handleChange} rows={4} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Brief description of your startup..." />
											</div>
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">Valuations</label>
												<textarea name="valuations" value={formData.valuations} onChange={handleChange} rows={2} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="e.g. Seed: $1M, Series A: $5M" />
											</div>
											<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
													<input type="url" name="website" value={formData.website} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="https://startup.com" />
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Founded Year <span className="text-red-500">*</span></label>
													<input type="number" name="foundedYear" value={formData.foundedYear} onChange={handleChange} required min="1900" max={new Date().getFullYear()} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="2024" />
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Industry <span className="text-red-500">*</span></label>
													<select name="industry" value={formData.industry} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
														<option value="">Select Industry</option>
														{industryOptions.length > 0 ? industryOptions.map(o => <option key={o.id} value={o.value}>{o.value}</option>) : ['Technology', 'Healthcare', 'Fintech', 'E-commerce', 'Education'].map(v => <option key={v} value={v}>{v}</option>)}
													</select>
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Stage <span className="text-red-500">*</span></label>
													<select name="stage" value={formData.stage} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
														<option value="">Select Stage</option>
														{stageOptions.length > 0 ? stageOptions.map(o => <option key={o.id} value={o.value}>{o.value}</option>) : ['Idea', 'MVP', 'Seed', 'Series A', 'Growth'].map(v => <option key={v} value={v}>{v}</option>)}
													</select>
												</div>
											</div>
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">Company Logo</label>
												<div className="flex items-center gap-4">
													{formData.logoPreview && <img src={formData.logoPreview} alt="Logo preview" className="w-20 h-20 object-cover rounded-lg border-2 border-gray-300" />}
													<input type="file" accept="image/*" onChange={handleLogoChange} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
												</div>
											</div>
										</div>
									</div>
								</div>
							)}

							{/* Step 2: Registration & Bank */}
							{currentStep === 2 && (
								<div className="space-y-6">
									<h2 className="text-lg font-semibold text-gray-900 mb-4">Registration Details</h2>
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">CIN (Corporate Identification Number)</label>
											<input type="text" name="cin" value={formData.cin} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="U12345AB2024PTC123456" />
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
											<input type="text" name="gstNumber" value={formData.gstNumber} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="22AAAAA0000A1Z5" />
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">PAN Number</label>
											<input type="text" name="panNumber" value={formData.panNumber} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase" placeholder="AAAAA1234A" />
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">DPIIT Recognition Number</label>
											<input type="text" name="dpiitNumber" value={formData.dpiitNumber} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="DIPP12345" />
										</div>
									</div>
									<div className="mt-6 pt-6 border-t border-gray-200">
										<div className="flex justify-between items-center mb-4">
											<h3 className="text-md font-semibold text-gray-900">Bank Account Details</h3>
											<button type="button" onClick={addBankAccount} className="text-sm font-medium text-blue-600 hover:text-blue-700">+ Add bank account</button>
										</div>
										<div className="space-y-6">
											{formData.bankAccounts.map((bank, index) => (
												<div key={index} className="border border-gray-200 rounded-lg p-4 relative bg-gray-50/50">
													{formData.bankAccounts.length > 1 && (
														<button type="button" onClick={() => removeBankAccount(index)} className="absolute top-2 right-2 text-red-600 hover:text-red-800 text-sm">Remove</button>
													)}
													<h4 className="font-medium text-gray-800 mb-3">Bank account {index + 1}</h4>
													<div className="space-y-4">
														<div>
															<label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name <span className="text-red-500">*</span></label>
															<input type="text" value={bank.accountHolderName} onChange={e => handleBankAccountChange(index, 'accountHolderName', e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="As per bank records" />
														</div>
														<div>
															<label className="block text-sm font-medium text-gray-700 mb-1">Bank Account Number <span className="text-red-500">*</span></label>
															<input type="text" value={bank.bankAccountNumber} onChange={e => handleBankAccountChange(index, 'bankAccountNumber', e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter bank account number" />
														</div>
														<IFSCVerification
															onVerified={bankDetails => {
																setFormData(prev => {
																	const next = [...prev.bankAccounts];
																	next[index] = { ...next[index], ifscCode: bankDetails.ifscCode, bankName: bankDetails.bankName, branchName: bankDetails.branchName };
																	return { ...prev, bankAccounts: next };
																});
															}}
															initialValue={bank.ifscCode}
														/>
														{bank.bankName && (
															<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
																<div>
																	<label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
																	<input type="text" value={bank.bankName} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700" />
																</div>
																<div>
																	<label className="block text-sm font-medium text-gray-700 mb-1">Branch Name</label>
																	<input type="text" value={bank.branchName} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700" />
																</div>
															</div>
														)}
													</div>
												</div>
											))}
										</div>
									</div>
								</div>
							)}

							{/* Step 3: Location */}
							{currentStep === 3 && (
								<div className="space-y-6">
									<h2 className="text-lg font-semibold text-gray-900 mb-4">Company Location</h2>
									<div className="space-y-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Official Address <span className="text-red-500">*</span></label>
											<textarea name="officialAddress" value={formData.officialAddress} onChange={handleChange} required rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Registered office address" />
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Branch Address</label>
											<textarea name="branchAddress" value={formData.branchAddress} onChange={handleChange} rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Branch office address (if different)" />
										</div>
										<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">City <span className="text-red-500">*</span></label>
												<input type="text" name="city" value={formData.city} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Bangalore" />
											</div>
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">State <span className="text-red-500">*</span></label>
												<input type="text" name="state" value={formData.state} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Karnataka" />
											</div>
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">PIN Code <span className="text-red-500">*</span></label>
												<input type="text" name="pincode" value={formData.pincode} onChange={handleChange} required pattern="[0-9]{6}" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="560001" />
											</div>
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">Country <span className="text-red-500">*</span></label>
												<input type="text" name="country" value={formData.country} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
											</div>
										</div>
									</div>
								</div>
							)}

							{/* Step 4: Directors */}
							{currentStep === 4 && (
								<div className="space-y-6">
									<div className="flex justify-between items-center">
										<h2 className="text-lg font-semibold text-gray-900">Directors / Founders</h2>
										<button type="button" onClick={addDirector} className="text-sm font-medium text-blue-600 hover:text-blue-700">+ Add director</button>
									</div>
									{formData.directors.map((dir, index) => (
										<div key={index} className="border border-gray-200 rounded-lg p-4 relative">
											{formData.directors.length > 1 && (
												<button type="button" onClick={() => removeDirector(index)} className="absolute top-2 right-2 text-red-600 hover:text-red-800 text-sm">Remove</button>
											)}
											<h3 className="font-medium text-gray-900 mb-3">Director {index + 1}</h3>
											<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
													<input type="text" value={dir.name} onChange={e => handleDirectorChange(index, 'name', e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Director name" />
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
													<select value={dir.category || ''} onChange={e => handleDirectorChange(index, 'category', e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
														<option value="">Select Category</option>
														<option value="PROMOTORS">Promotors</option>
														<option value="INVESTORS">Investors</option>
														<option value="DIRECTORS">Directors</option>
														<option value="BOARD_OBSERVERS">Board Observers</option>
														<option value="INDEPENDENT_DIRECTORS">Independent Directors</option>
														<option value="FOUNDER_DIRECTORS">Founder Directors</option>
													</select>
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Designation <span className="text-red-500">*</span></label>
													<input type="text" value={dir.designation} onChange={e => handleDirectorChange(index, 'designation', e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="CEO, CTO, etc." />
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
													<input type="email" value={dir.email} onChange={e => handleDirectorChange(index, 'email', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="director@startup.com" />
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
													<input type="tel" value={dir.phone} onChange={e => handleDirectorChange(index, 'phone', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="+91 98765 43210" />
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Shareholding %</label>
													<input type="number" value={dir.shareholding} onChange={e => handleDirectorChange(index, 'shareholding', e.target.value)} min="0" max="100" step="0.01" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="25.5" />
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">PAN Number</label>
													<input type="text" value={dir.panNumber || ''} onChange={e => handleDirectorChange(index, 'panNumber', e.target.value.toUpperCase())} maxLength={10} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 uppercase" placeholder="ABCDE1234F" />
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-1">Aadhar Number</label>
													<input type="text" value={dir.aadharNumber || ''} onChange={e => handleDirectorChange(index, 'aadharNumber', e.target.value.replace(/\D/g, ''))} maxLength={12} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="12 digits" />
												</div>
											</div>
										</div>
									))}
								</div>
							)}

							<div className="flex flex-col sm:flex-row justify-between gap-4 pt-6 border-t border-gray-200">
								<button type="button" onClick={prevStep} disabled={currentStep === 1} className="order-2 sm:order-1 px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
									Previous
								</button>
								<div className="flex gap-3 order-1 sm:order-2">
									{currentStep < 4 ? (
										<button type="button" onClick={nextStep} className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg shadow hover:from-blue-700 hover:to-indigo-700 transition">
											Next
										</button>
									) : (
										<button type="submit" disabled={saving} className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg shadow hover:from-blue-700 hover:to-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
											{saving ? 'Saving...' : 'Save and go to dashboard'}
										</button>
									)}
								</div>
							</div>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default StartupCompleteProfile;
